import React, { useState, useEffect, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import SubTitle from '../common/SubTitle';
import Cards from '../common/Cards';
import SearchFilter from '../common/SearchFilter';
import './Vehicle.css';
import Pagination from '../common/Pagination';
import axios from 'axios';
import config from '../../config.json';
import activeEmoji from "../../images/vehicle/Active_icon.svg";
import inActiveEmoji from "../../images/vehicle/Inactive_icon.svg";
import deadEmoji from "../../images/vehicle/Dead_icon.svg";
import Update_Icon from "../../images/table/Update_icon.svg";
import Track_Icon from "../../images/table/Track_icon.svg";
import Delete_Icon from "../../images/table/Delete_icon.svg";
import DeleteModal from '../common/DeleteModal';
import DynamicTable from '../common/DynamicTable';
import { usePermission } from "../../hooks/usePermission";
import { useTranslation } from 'react-i18next';
import { CARD_BACKGROUND_COLORS, TEXT_COLORS } from '../../constants/styles';
import { ACTIONS_COLUMN_ID } from "../../constants/dashboardConstants";

const headers = [
    { key: "vehicleID", value: 'Vehicle ID' },
    { key: "vehicleName", value: 'Vehicle Name' },
    { key: "siteID", value: 'Site ID' },
    { key: "routes", value: 'Routes' },
    { key: "drivers", value: 'Drivers' },
    { key: "status", value: 'Status' },
    { key: ACTIONS_COLUMN_ID, value: 'Actions' }
];

export default function VehicleMain() {
    const { t } = useTranslation()
    const [vehicleData, setVehicleData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(true)
    const [filters, setFilters] = useState({
        route: '',
        status: '',
    });
    const navigate = useNavigate();
    const { hasPermission } = usePermission();

    // Generate actions based on permissions
    const getCustomActions = () => {
        const actions = [];

        // Add update action if user has permission
        if (hasPermission("/api/vehicle", "PUT")) {
            actions.push({
                id: 'update',
                label: 'Update',
                icon: Update_Icon,
                path: '/vehicle/add'
            });
        }

        // Add track action (assuming it's a GET operation on vehicle/id)
        if (hasPermission("/api/vehicle/id", "GET")) {
            actions.push({
                id: 'track',
                label: 'Track',
                icon: Track_Icon,
                path: '/vehicle/track'
            });
        }

        // Add delete action if user has permission
        if (hasPermission("/api/vehicle", "DELETE")) {
            actions.push({
                id: 'delete',
                label: 'Delete',
                className: 'delete',
                icon: Delete_Icon,
                path: ''
            });
        }

        return actions;
    };

    // Delete modal state
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [vehicleToDelete, setVehicleToDelete] = useState(null);

    // Define your filters configuration
    const [filterConfig, setFilterConfig] = useState([
        {
            id: 'status',
            label: 'Status',
            helperText: 'Select vehicle status',
            options: [
                { value: 'ACTIVE', label: 'active' },
                { value: 'INACTIVE', label: 'inactive' },
                { value: 'DEAD', label: 'dead' },

            ]
        },
        {
            id: 'route',
            label: 'route',
            helperText: 'Select route',
            options: []
        }
    ]);

    // Fetch vehicle data from API on component mount
    useEffect(() => {
        // Check if user has permission to view vehicles
        // if (!hasPermission("/api/vehicle/organization", "GET")) {
        //     console.log("User doesn't have permission to view vehicles");
        //     // You could redirect or show an error message
        //     return;
        // }
        axios
            .get(`${config.baseURL}/vehicle/organization`, { withCredentials: true })
            .then((response) => {
                console.log("response", response.data);
                if (!Array.isArray(response.data)) {
                    console.error("Unexpected data format: expected array", response.data);
                    return;
                }

                setVehicleData(response.data);

                // Extract routes from the data - let's log a sample vehicle to check structure
                const sampleVehicle = response.data[0];
                console.log("Sample vehicle for route checking:", sampleVehicle);

                // Extract unique routes by analyzing the response data structure
                const uniqueRoutes = [];
                const routeIds = new Set();

                response.data.forEach(vehicle => {
                    // Debug log to inspect route structure
                    if (vehicle.route) {
                        console.log("Found route:", vehicle.route);
                    }

                    // Get route ID regardless of casing (Id, id, ID)
                    const routeId = vehicle.route?.Id || vehicle.route?.id || vehicle.route?.ID;
                    const routeName = vehicle.route?.name || vehicle.route?.Name || `Route ${routeId}`;

                    if (routeId && !routeIds.has(routeId)) {
                        routeIds.add(routeId);
                        uniqueRoutes.push({
                            value: routeId.toString(), // Ensure it's a string
                            label: routeName
                        });
                    }
                });

                console.log("Generated route options:", uniqueRoutes);

                // Update filter config with dynamic routes
                setFilterConfig(prev => {
                    const updated = [...prev];
                    const routeIndex = updated.findIndex(f => f.id === 'route');
                    if (routeIndex >= 0) {
                        updated[routeIndex].options = uniqueRoutes;
                    }
                    return updated;
                });
                setIsLoading(false)
            })
            .catch((error) => {
                if (error.response && error.response.status === 401) {
                    console.log("Unauthorized request");
                    navigate("/");
                    return;
                }
                console.error("Error fetching vehicle data:", error);
            });
    }, [navigate]);

    const formatDataForTable = (data) => {
        return data.map(vehicle => {
            // Create a formatted object for display using key properties
            const formattedData = {
                vehicleID: vehicle.device_id || 'N/A',
                vehicleName: vehicle.name || 'N/A',
                siteID: vehicle.site?.siteId || 'N/A',
                routes: vehicle.route?.name || 'N/A',
                drivers: vehicle.driver?.first_name || 'N/A',
                status: (
                    <div className="status-emoji">
                        {vehicle.status === "ACTIVE" && <img src={activeEmoji} alt="ACTIVE" title="Active" />}
                        {vehicle.status === "INACTIVE" && <img src={inActiveEmoji} alt="INACTIVE" title="Inactive" />}
                        {(vehicle.status === "DEAD" || !vehicle.status) && <img src={deadEmoji} alt="DEAD" title="Dead" />}
                    </div>
                ),
            };

            // Add original ID for action handling
            formattedData.id = vehicle.id;
            formattedData.originalData = vehicle;

            return formattedData;
        });
    };
    // Handle search changes
    const handleSearchChange = (value) => {
        setSearchQuery(value);
        console.log('Search value:', value);
    };

    // Handle filter changes
    const handleFilterChange = (filterValues) => {
        setFilters(filterValues);
        console.log('Filter values:', filterValues);
    };

    // Filter data based on search query and filters
    const filteredVehicleData = vehicleData.filter((vehicle) => {
        // Get route ID regardless of casing
        const routeId = vehicle.route?.Id || vehicle.route?.id || vehicle.route?.ID;

        // First check if any filter is applied
        const routeMatch = !filters.route || (routeId && String(routeId) === String(filters.route));
        // const statusMatch = !filters.status || vehicle.status === filters.status;

        // Updated status match logic to properly handle DEAD and null/undefined status
        let statusMatch = true;
        if (filters.status) {
            if (filters.status === 'DEAD') {
                // For DEAD status, include both explicitly DEAD and null/undefined status
                statusMatch = vehicle.status === 'DEAD' || !vehicle.status;
            } else {
                statusMatch = vehicle.status === filters.status;
            }
        }

        // Then check search term if filters passed
        if (!routeMatch || !statusMatch) {
            return false;
        }

        // If no search query, return true (show all that match filters)
        if (!searchQuery) {
            return true;
        }

        // Otherwise, search in all relevant fields
        const query = searchQuery.toLowerCase();
        return (
            (vehicle.device_id && vehicle.device_id.toLowerCase().includes(query)) ||
            (vehicle.name && vehicle.name.toLowerCase().includes(query)) ||
            (vehicle.site?.siteId && vehicle.site.siteId.toLowerCase().includes(query)) ||
            (vehicle.route?.name && vehicle.route.name.toLowerCase().includes(query)) ||
            (vehicle.driver?.first_name && vehicle.driver.first_name.toLowerCase().includes(query)) ||
            (vehicle.status && vehicle.status.toLowerCase().includes(query))
        );
    });

    // Track all vehicles handler
    const handleTrackAllVehicles = () => {
        // if (hasPermission("/api/vehicle/organization", "GET")) {
        navigate("/vehicle/TrackAllVehicle", {
            state: { allVehicleData: vehicleData }
        });
        // } else {
        //     console.log("User doesn't have permission to track all vehicles");
        //     // Could add notification here
        // }
    };

    // Add vehicle handler - check permission
    const handleAddVehicle = () => {
        if (hasPermission("/api/vehicle", "POST")) {
            navigate("/vehicle/add");
        } else {
            console.log("User doesn't have permission to add vehicles");
            // Could add notification here
        }
    };

    // Delete modal handlers
    const handleDeleteCancel = () => {
        setIsDeleteModalOpen(false);
        setVehicleToDelete(null);
    };

    const handleDeleteConfirm = async () => {
        if (vehicleToDelete) {
            console.log("Deleting vehicle ID:", vehicleToDelete.id);
            // Check permission before deletion
            if (!hasPermission("/api/vehicle", "DELETE")) {
                console.log("User doesn't have permission to delete vehicles");
                setIsDeleteModalOpen(false);
                setVehicleToDelete(null);
                return;
            }
            try {
                const userState = localStorage.getItem('userState');
                const parsedUserState = JSON.parse(userState);
                const orgId = parsedUserState.user.organizationId
                const response = await axios.delete(`${config.baseURL}/vehicle`, {
                    data: { vehicleId: vehicleToDelete.id, organizationId: orgId },
                });
                console.log("Vehicle deleted successfully:", response.data);
                window.location.reload();

            } catch (error) {
                console.log("Error in deleting vehicle data: ", error);
                throw error;
            }
        }
        setIsDeleteModalOpen(false);
        setVehicleToDelete(null);
    };

    const handleActionClick = (actionId, rowIndex, actionObj) => {
        // Get the actual vehicle data for this row
        const vehicle = filteredVehicleData[rowIndex].originalData || filteredVehicleData[rowIndex];

        switch (actionId) {
            case "update":
                // Check permission before navigating to update
                if (hasPermission("/api/vehicle", "PUT")) {
                    navigate(actionObj.path, { state: vehicle.id });
                } else {
                    console.log("User doesn't have permission to update vehicles");
                    // You could show a notification here
                }
                break;
            case "track":
                // Check permission before navigating to track
                if (hasPermission("/api/vehicle/id", "GET")) {
                    navigate(actionObj.path, { state: vehicle.id });
                } else {
                    console.log("User doesn't have permission to track vehicles");
                    // You could show a notification here
                }
                break;
            case "delete":
                // Check permission before opening delete modal
                if (hasPermission("/api/vehicle", "DELETE")) {
                    setVehicleToDelete(vehicle);
                    setIsDeleteModalOpen(true);
                } else {
                    console.log("User doesn't have permission to delete vehicles");
                    // You could show a notification here
                }
                break;
            default:
                break;
        }
    };

    // Check if user has permission to see the actions column
    const showActionsColumn = hasPermission("/api/vehicle", "PUT") ||
        hasPermission("/api/vehicle/id", "GET") ||
        hasPermission("/api/vehicle", "DELETE");

    // Filter headers to remove Actions if user has no action permissions
    const filteredHeaders = showActionsColumn ?
        headers :
        headers.filter(h => h.key !== ACTIONS_COLUMN_ID);
    // Determine if the user has permission to add or track vehicles
    const canAddVehicle = hasPermission("/api/vehicle", "POST");
    //   const canTrackVehicles = hasPermission("/api/vehicle/organization", "GET");
    return (
        <>
            <SubTitle
                title="Vehicles"
                path="organization > Vehicles"
                add={canAddVehicle ? "createVehicle" : ""}
                addPath={canAddVehicle ? "/vehicle/add" : ""}
                track={"monitorVehicles"}
                trackPath={"/vehicle/TrackAllVehicle"}
                onAddClick={canAddVehicle ? handleAddVehicle : null}
                onTrackClick={handleTrackAllVehicles}
            />

            <Cards cardData={[
                {
                    title: "totalVehicles",
                    value: vehicleData?.length || 0,
                    backgroundColor: CARD_BACKGROUND_COLORS.DARK_BLUE,
                    titleColor: TEXT_COLORS.WHITE,
                    valueColor: TEXT_COLORS.WHITE
                },
                {
                    title: "activeVehicles",
                    value: vehicleData?.filter(vehicle => vehicle.status === "ACTIVE").length || 0,
                    backgroundColor: CARD_BACKGROUND_COLORS.GREEN,
                    titleColor: TEXT_COLORS.WHITE,
                    valueColor: TEXT_COLORS.WHITE
                },
                {
                    title: "inactiveVehicles",
                    value: vehicleData?.filter(vehicle => vehicle.status === "INACTIVE").length || 0,
                    backgroundColor: CARD_BACKGROUND_COLORS.YELLOW,
                    titleColor: TEXT_COLORS.BLACK,
                    valueColor: TEXT_COLORS.BLACK
                },
                {
                    title: "deadVehicles",
                    value: vehicleData?.filter(vehicle => vehicle.status === "DEAD" || !vehicle.status).length || 0,
                    backgroundColor: CARD_BACKGROUND_COLORS.RED,
                    titleColor: TEXT_COLORS.WHITE,
                    valueColor: TEXT_COLORS.WHITE
                }
            ]} />

            <div className="table_container">
                <SearchFilter
                    filters={filterConfig}
                    searchPlaceholder="search"
                    onSearchChange={handleSearchChange}
                    onFilterChange={handleFilterChange}
                />

                <Suspense fallback={<div className="loader"></div>}>
                    <DynamicTable
                        headers={filteredHeaders}
                        data={formatDataForTable(filteredVehicleData || [])}
                        onActionClick={handleActionClick}
                        actions={getCustomActions()}
                        loading={isLoading}
                    />
                </Suspense>

                {/* <Pagination
                    totalItems={filteredVehicleData?.length || 0}
                /> */}
            </div>
            <DeleteModal
                isOpen={isDeleteModalOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title="Delete Vehicle!"
                message={
                    <>
                        {t("Are you sure you want to delete")}{' '}
                        <span style={{ fontWeight: 600, color: "#d32f2f" }}
                        >{vehicleToDelete?.name || t('this vehicle')}</span>?{' '}
                        {t("This action cannot be undone.")}
                    </>
                }
            />

        </>
    );
}

